import { skipToken } from '@reduxjs/toolkit/query';
import {
    ArrowDownIcon,
    ArrowUpIcon,
    ChartColumnIcon,
    ChartLineIcon,
    ChartPieIcon,
    type SvgIconComponent,
    TableGridIcon,
} from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { formatToHours } from 'components/util/HoursDisplay';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { format, isValid, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { useReadAiChatFigureRowsQuery } from 'store/api/aiChatFigureApi';
import type { ChartSpec } from 'types/aiChat';
import type { FigureCell, FigureColumnType, FigureDefinition, FigureRows, FigureRowsColumn } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { dateStringToDateObject } from 'utils/parseDate';
import { chartFigureRows, chartKeyNumbers, chartPeak, chartTotals, chartValueCell } from '../../../utils/chartSeries';
import { inlineChartSpec, sourcedChartSpec } from '../../../utils/chartSpec';
import ChartErrorBoundary from '../ChartErrorBoundary';
import { ChartPlot, hasChartData } from '../ChartToolPart';
import { FigureCellValue } from './FigureCellValue';
import { FigureCopyButton } from './FigureCopyButton';
import { FigureDialog } from './FigureDialog';
import { FigureDownload, useFigureExport } from './FigureDownload';
import { FigureFrame } from './FigureFrame';
import { columnLabel, figureCount, figureSort, figureTile } from './figureMeta';
import { cellText, isMoneyCell } from './figureRows';
import { FigureReveal, FigureStatus } from './RowsFigure';

const CHART_ICONS: Record<ChartSpec['type'], SvgIconComponent> = {
    bar: ChartColumnIcon,
    line: ChartLineIcon,
    pie: ChartPieIcon,
};

const NUMERIC: FigureColumnType[] = ['number', 'hours', 'money', 'percentage'];

// An aggregation's month fields hold the month's number, 1 to 12.
const MONTH_FIELD = /month$/i;

const STATE_CLASS_NAME = 'text-meta text-foreground-secondary px-4 pb-4';

// A view once drawn stays drawn, the one not chosen out of the flow and unseen at the box's width: a switch neither
// draws nor measures the chart again. Base UI marks a panel `data-ending-style` as it is left, then `data-hidden`.
const VIEW_PANEL_CLASS_NAME =
    'data-hidden:invisible data-hidden:absolute data-hidden:inset-x-0 data-hidden:top-0 data-ending-style:invisible data-ending-style:absolute data-ending-style:inset-x-0 data-ending-style:top-0';

// Cells at the box's edges keep clear of its border.
const CELL_CLASS_NAME = 'px-2 py-1.5 first:ps-3 last:pe-3';

// What the computed style of the chart's SVG must carry once it is an image of its own, away from the page's styles.
const SVG_STYLES = [
    'fill',
    'fill-opacity',
    'stroke',
    'stroke-width',
    'stroke-dasharray',
    'stroke-opacity',
    'opacity',
    'visibility',
    'font-family',
    'font-size',
    'font-weight',
    'text-anchor',
    'dominant-baseline',
];

/** Whether this browser can put an image on the clipboard: Copy chart shows only where it can. */
const canCopyImage = () =>
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator.clipboard?.write === 'function' &&
    ((ClipboardItem as { supports?: (type: string) => boolean }).supports?.('image/png') ?? true);

/**
 * The plot as a PNG at twice its size on the card's background: its SVG with the computed colours and type written
 * in, then its legend, which the chart draws as HTML over the SVG, painted where it shows.
 */
const chartPng = async (plot: HTMLElement): Promise<Blob> => {
    const svg = plot.querySelector('svg');
    if (!svg) {
        throw new Error('The chart has no plot to copy');
    }
    const box = svg.getBoundingClientRect();
    const clone = svg.cloneNode(true) as SVGSVGElement;
    const originals = [svg, ...svg.querySelectorAll('*')];
    [clone, ...clone.querySelectorAll('*')].forEach((node, index) => {
        const style = getComputedStyle(originals[index]);
        node.setAttribute('style', SVG_STYLES.map((name) => `${name}:${style.getPropertyValue(name)}`).join(';'));
    });
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(box.width));
    clone.setAttribute('height', String(box.height));
    const image = new Image();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(clone))}`;
    await image.decode();

    const canvas = document.createElement('canvas');
    canvas.width = box.width * 2;
    canvas.height = box.height * 2;
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('No canvas to draw the chart on');
    }
    context.scale(2, 2);
    context.fillStyle = getComputedStyle(plot).getPropertyValue('--card').trim();
    context.fillRect(0, 0, box.width, box.height);
    context.drawImage(image, 0, 0, box.width, box.height);
    // The legend's swatches are its only styled elements; each one's label follows it in its entry.
    plot.querySelectorAll<HTMLElement>('.recharts-legend-wrapper div[style]').forEach((swatch) => {
        const at = swatch.getBoundingClientRect();
        const entry = getComputedStyle(swatch.parentElement ?? swatch);
        context.fillStyle = getComputedStyle(swatch).backgroundColor;
        context.beginPath();
        context.roundRect(at.left - box.left, at.top - box.top, at.width, at.height, 3);
        context.fill();
        context.fillStyle = entry.color;
        context.font = `${entry.fontWeight} ${entry.fontSize} ${entry.fontFamily}`;
        context.textBaseline = 'middle';
        context.fillText(
            swatch.parentElement?.textContent ?? '',
            at.right - box.left + parseFloat(entry.columnGap || '0'),
            at.top - box.top + at.height / 2
        );
    });
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('The chart could not be drawn'))),
            'image/png'
        );
    });
};

/** A value as a screen reader hears it in the chart's summary and the tooltip gives it, in the format its cells show. */
const valueText = (intl: IntlShape, value: number, type: FigureColumnType, currency: string | null): string => {
    if (type === 'hours') {
        return formatToHours(value);
    }
    if (type === 'percentage') {
        // A table cell's percent, a space before the sign: "38.2 %".
        return `${intl.formatNumber(value * 100, { maximumFractionDigits: 1 })}\u00a0%`;
    }
    const amount = intl.formatNumber(value, { maximumFractionDigits: 2 });
    return type === 'money' && currency ? `${amount} ${currency}` : amount;
};

/** The latest total's change against the earlier one: green up, red down, neutral when level. */
function ChangeChip({ change }: { change: number }) {
    const intl = useIntl();
    let tone = 'bg-neutral-tint text-foreground-secondary';
    let Arrow: SvgIconComponent | null = null;
    if (change > 0) {
        tone = 'bg-success-tint text-success-ink';
        Arrow = ArrowUpIcon;
    } else if (change < 0) {
        tone = 'bg-danger-tint text-danger-ink';
        Arrow = ArrowDownIcon;
    }
    return (
        <span
            className={cn(
                'text-caption inline-flex items-center gap-0.5 rounded-lg px-1.5 font-medium tabular-nums',
                tone
            )}
        >
            {Arrow ? <Arrow className="size-3" /> : null}
            {/* Signed, to a tenth, a space before the sign: "+12.3 %". */}
            {`${intl.formatNumber(change * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'exceptZero' })}\u00a0%`}
        </span>
    );
}

/** The chart's values in the same box as the plot: a row per category, a column per series, and their totals. */
function ChartTable({ spec, table, currency }: { spec: ChartSpec; table: FigureRows; currency: string | null }) {
    const intl = useIntl();
    const [category, ...values] = table.columns;
    const totals = chartTotals(spec);
    // The latest series reads first, as the key numbers do.
    const valueClassName = (index: number) =>
        cn(
            CELL_CLASS_NAME,
            'text-end whitespace-nowrap',
            index === values.length - 1 ? 'text-foreground font-medium' : 'text-foreground-secondary'
        );

    return (
        <table className="text-meta w-full border-collapse tabular-nums">
            <thead>
                <tr className="bg-surface-subtle text-caption text-foreground-tertiary">
                    <th scope="col" className={cn(CELL_CLASS_NAME, 'text-start font-medium')}>
                        {category.label}
                    </th>
                    {values.map((column) => (
                        <th key={column.field} scope="col" className={cn(CELL_CLASS_NAME, 'text-end font-medium')}>
                            {column.label}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {table.rows.map((row, rowIndex) => (
                    <tr
                        // Categories may repeat, and the rows never reorder: a fresh chart is a fresh table.
                        key={rowIndex}
                        className="border-divider-soft hover:bg-row-stripe border-t"
                    >
                        <th scope="row" className={cn(CELL_CLASS_NAME, 'text-foreground text-start font-normal')}>
                            {cellText(row[0] ?? null)}
                        </th>
                        {values.map((column, index) => (
                            <td key={column.field} className={valueClassName(index)}>
                                <FigureCellValue cell={row[index + 1] ?? null} column={column} />
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            {/* Shares do not add up to anything, so a chart of percentages has no total. */}
            {values[0]?.type === 'percentage' ? null : (
                <tfoot>
                    <tr className="border-border border-t font-semibold">
                        <th scope="row" className={cn(CELL_CLASS_NAME, 'text-foreground text-start')}>
                            {intl.formatMessage({ id: 'ai-chat.figure.total' })}
                        </th>
                        {values.map((column, index) => (
                            <td
                                key={column.field}
                                className={cn(CELL_CLASS_NAME, 'text-foreground text-end whitespace-nowrap')}
                            >
                                <FigureCellValue
                                    cell={chartValueCell(totals[index] ?? 0, column.type, currency)}
                                    column={column}
                                />
                            </td>
                        ))}
                    </tr>
                </tfoot>
            )}
        </table>
    );
}

interface ViewProps {
    spec: ChartSpec;
    /** The chart's values as rows: the Table view, and what Copy table as CSV and Download hold. */
    table: FigureRows;
    copyCsv: () => Promise<void>;
    /** The full-size dialog's taller plot. */
    tall?: boolean;
}

interface SwitchProps {
    /** At the start of the switch's row: the key numbers. */
    keyNumbers: ReactNode;
    plot: ReactNode;
    values: ReactNode;
    /** The copy button under the box, for each view. */
    copy: Record<'chart' | 'table', ReactNode>;
}

/**
 * The Chart / Table switch and the box its views share, so the switch never moves the frame; the copy button for the
 * view under the box. The box takes the chosen view's height on card resize's clock. Its parts come drawn from the
 * Figure, so a switch re-renders the tabs and the box alone, never the chart or the table.
 */
function ChartSwitch({ keyNumbers, plot, values, copy }: SwitchProps) {
    const intl = useIntl();
    const [view, setView] = useState<'chart' | 'table'>('chart');
    // The table is drawn the first time it is chosen, then kept with the plot: opening full size draws the plot alone.
    const [tableDrawn, setTableDrawn] = useState(false);
    const viewsRef = useRef<HTMLDivElement>(null);
    // Unset until measured, so the first height is drawn at once.
    const [viewsHeight, setViewsHeight] = useState<number>();
    useEffect(() => {
        const views = viewsRef.current;
        if (!views) {
            return undefined;
        }
        const observer = new ResizeObserver(([entry]) => setViewsHeight(entry.borderBoxSize[0].blockSize));
        observer.observe(views);
        return () => observer.disconnect();
    }, []);

    return (
        <Tabs
            value={view}
            onValueChange={(next: 'chart' | 'table') => {
                setView(next);
                setTableDrawn((drawn) => drawn || next === 'table');
            }}
            className="gap-0"
        >
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4">
                {keyNumbers}
                <TabsList className="ms-auto">
                    <TabsTrigger value="chart">
                        <ChartColumnIcon className="hidden size-3.5 @sm:block" />
                        {intl.formatMessage({ id: 'ai-chat.figure.chart' })}
                    </TabsTrigger>
                    <TabsTrigger value="table">
                        <TableGridIcon className="hidden size-3.5 @sm:block" />
                        {intl.formatMessage({ id: 'ai-chat.figure.table' })}
                    </TabsTrigger>
                </TabsList>
            </div>
            <div
                className="t-resize border-border mx-4 mt-3 mb-1.5 box-content overflow-hidden rounded-[10px] border"
                style={{ height: viewsHeight }}
            >
                <div ref={viewsRef} className="relative">
                    <TabsContent
                        value="chart"
                        keepMounted
                        hidden={false}
                        className={cn('px-3 pt-3 pb-2.5', VIEW_PANEL_CLASS_NAME)}
                    >
                        {plot}
                    </TabsContent>
                    <TabsContent
                        value="table"
                        keepMounted={tableDrawn}
                        hidden={false}
                        className={cn('overflow-x-auto', VIEW_PANEL_CLASS_NAME)}
                    >
                        {values}
                    </TabsContent>
                </div>
            </div>
            {copy[view] ? (
                <div key={view} className="flex items-center gap-1 px-2 pb-1.5">
                    {copy[view]}
                </div>
            ) : null}
        </Tabs>
    );
}

/** A chart Figure's body: the key numbers, and the plot and its table behind the Chart / Table switch. */
function ChartView({ spec, table, copyCsv, tall }: ViewProps) {
    const intl = useIntl();
    const plotId = useId();
    const values = table.columns.slice(1);
    const { type } = values[0];
    const currency = table.rows.flat().find(isMoneyCell)?.currency ?? null;
    const { total, change } = chartKeyNumbers(spec);
    const latest = spec.type === 'pie' ? null : values.at(-1)?.label;
    const peak = chartPeak(spec);
    const summary = intl.formatMessage(
        { id: 'ai-chat.figure.chart-summary' },
        {
            chart: intl.formatMessage({ id: 'ai-chat.chart.summary' }, { type: spec.type, count: table.rows.length }),
            category: peak?.category ?? '',
            value: peak ? valueText(intl, peak.value, type, currency) : '',
        }
    );
    const fallback = (
        <p className="text-meta text-error">{intl.formatMessage({ id: 'ai-chat.error.chart-render-failed' })}</p>
    );
    const copyChart = () => {
        const element = document.getElementById(plotId);
        return element
            ? navigator.clipboard.write([new ClipboardItem({ 'image/png': chartPng(element) })])
            : Promise.reject(new Error('The chart has no plot to copy'));
    };

    return (
        <ChartSwitch
            keyNumbers={
                // A total of shares means nothing, so a chart of percentages has no key numbers.
                type === 'percentage' ? null : (
                    <div className="flex items-center gap-2">
                        <span className="text-section text-foreground whitespace-nowrap tabular-nums">
                            {latest ? <span className="sr-only">{latest}: </span> : null}
                            {type === 'hours' ? (
                                // A headline: whole hours, grouped, the unit quieter ("1 660 h"); the table keeps
                                // them exact.
                                <>
                                    <MoneyDisplay amount={Math.round(total)} decimalScale={0} />
                                    <span className="text-foreground-tertiary font-normal">
                                        {intl.formatMessage({ id: 'time.hours-suffix' })}
                                    </span>
                                </>
                            ) : (
                                <FigureCellValue
                                    cell={chartValueCell(total, type, currency)}
                                    column={values[values.length - 1]}
                                />
                            )}
                        </span>
                        {change === null ? null : <ChangeChip change={change} />}
                    </div>
                )
            }
            plot={
                <ChartErrorBoundary
                    resetKey={JSON.stringify(spec)}
                    fallback={fallback}
                    onError={(error) =>
                        console.error('Failed to render a chart Figure in AI chat', { error, type: spec.type })
                    }
                >
                    <div id={plotId}>
                        <ChartPlot
                            spec={spec}
                            label={summary}
                            fallback={fallback}
                            tall={tall}
                            formatValue={(value) => valueText(intl, value, type, currency)}
                        />
                    </div>
                </ChartErrorBoundary>
            }
            values={<ChartTable spec={spec} table={table} currency={currency} />}
            copy={{
                chart: canCopyImage() ? (
                    <FigureCopyButton
                        label={intl.formatMessage({ id: 'ai-chat.figure.copy-chart' })}
                        copy={copyChart}
                    />
                ) : null,
                table: (
                    <FigureCopyButton label={intl.formatMessage({ id: 'ai-chat.figure.copy-table' })} copy={copyCsv} />
                ),
            }}
        />
    );
}

type Props = { definition: FigureDefinition; spec?: undefined } | { spec: ChartSpec; definition?: undefined };

/**
 * A chart Figure: a `showFigure` chart drawn from its source's rows read again for the viewer, or from its own values
 * as of the day it was worked out, or an older chat's chart from its stored spec. Download and Open full size come with
 * a chart that has values to draw.
 */
export function ChartFigure({ definition, spec: storedSpec }: Props) {
    const intl = useIntl();
    const dateFormat = useEffectiveDateFormat();
    const source = definition?.source;
    const query = useReadAiChatFigureRowsQuery(definition && source ? definition : skipToken);
    const rows = query.data;
    const columnAt = (field: string | undefined) => rows?.columns.find((column) => column.field === field);
    const valueColumn = columnAt(definition?.y);
    const categoryColumn = columnAt(definition?.x);
    const valueIndex = rows && valueColumn ? rows.columns.indexOf(valueColumn) : -1;
    const currency = rows?.rows.map((row) => row[valueIndex] ?? null).find(isMoneyCell)?.currency ?? null;
    const type: FigureColumnType = valueColumn && NUMERIC.includes(valueColumn.type) ? valueColumn.type : 'number';
    const valueLabel = valueColumn ? columnLabel(valueColumn) : intl.formatMessage({ id: 'ai-chat.chart.value' });

    // A category or series as the chart names it: a month by its name, a day in the firm's date format.
    const cellLabel = (cell: FigureCell, column: FigureRowsColumn): string => {
        if (
            typeof cell === 'number' &&
            MONTH_FIELD.test(column.field) &&
            Number.isInteger(cell) &&
            cell >= 1 &&
            cell <= 12
        ) {
            return intl.formatDate(Date.UTC(2000, cell - 1, 1), { month: 'short', timeZone: 'UTC' });
        }
        if (column.type === 'date' && typeof cell === 'string' && isValid(parseISO(cell))) {
            return format(parseISO(cell), dateFormat);
        }
        return cellText(cell);
    };

    let spec: ChartSpec | null = storedSpec ?? null;
    if (definition && !source) {
        spec = inlineChartSpec(definition);
    } else if (definition && rows) {
        const other = intl.formatMessage({ id: 'ai-chat.figure.other' });
        spec = sourcedChartSpec(definition, rows, { cell: cellLabel, value: valueLabel, other });
    }
    const drawable = !!spec && hasChartData(spec);
    const table =
        spec && drawable
            ? chartFigureRows(
                  spec,
                  { category: categoryColumn ? columnLabel(categoryColumn) : '', value: valueLabel },
                  type,
                  currency
              )
            : null;

    const title = definition?.title ?? storedSpec?.title;
    // The file and the copied CSV say the rows were cut, as the meta line does.
    const note = definition && rows?.partial ? figureCount(intl, definition, rows) : null;
    const { copyCsv, generating, download } = useFigureExport(title ?? '', table, note);

    let meta: ReactNode = storedSpec?.description ?? null;
    if (definition && rows) {
        const sort = figureSort(intl, definition, rows);
        meta = (
            <>
                {figureCount(intl, definition, rows)}
                {sort ? ` · ${sort}` : null}
            </>
        );
    } else if (definition?.asOf) {
        meta = (
            <FormattedMessage
                id="ai-chat.figure.as-of"
                values={{ date: <FormattedDate value={dateStringToDateObject(definition.asOf)} /> }}
            />
        );
    }
    const header = {
        icon: CHART_ICONS[spec?.type ?? definition?.chartType ?? 'bar'],
        tileClassName: definition ? figureTile(definition).className : NEUTRAL_TILE_CLASS,
        title,
    };

    let actions: ReactNode = null;
    let body: ReactNode;
    const empty = !!rows && rows.rows.length === 0;
    if (definition && (empty || (source && !rows))) {
        body = <FigureStatus definition={definition} empty={empty} query={query} />;
    } else if (spec && table) {
        const downloadMenu = (
            <FigureDownload
                columns={table.columns.flatMap(({ label }) => (label ? [label] : []))}
                generating={generating}
                onDownload={download}
            />
        );
        actions = (
            <>
                {downloadMenu}
                <FigureDialog header={{ ...header, meta }} actions={downloadMenu}>
                    <ChartView spec={spec} table={table} copyCsv={copyCsv} tall />
                </FigureDialog>
            </>
        );
        body = <ChartView spec={spec} table={table} copyCsv={copyCsv} />;
    } else {
        body = (
            <p className={STATE_CLASS_NAME}>
                {intl.formatMessage({ id: spec ? 'ai-chat.chart.no-data' : 'ai-chat.error.chart-render-failed' })}
            </p>
        );
    }
    const loading = !!source && !rows && !query.isError;

    return (
        <FigureFrame {...header} meta={table ? meta : null} busy={loading} actions={actions} className="my-3">
            {/* Keyed by the error, so a retry shows the bars again. */}
            <FigureReveal key={query.isError ? 'error' : 'rows'} loading={loading}>
                {body}
            </FigureReveal>
        </FigureFrame>
    );
}
